/**
 * API utilities for consistent URL handling across environments
 */

import { getConfig } from '../config';

/**
 * Extract a human-readable message from a backend error response.
 * Handles both the legacy shape ({detail: "..."}) and the wrapped shape
 * emitted by backend/utils/error_handlers.py ({error: {message: "..."}}).
 * FastAPI validation errors put a list under `detail`.
 */
export const extractErrorMessage = (
  errorData: any,
  fallback: string = 'An unexpected error occurred.'
): string => {
  if (!errorData || typeof errorData !== 'object') return fallback;
  if (typeof errorData.detail === 'string') return errorData.detail;
  if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
    const first = errorData.detail[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object' && typeof first.msg === 'string') return first.msg;
  }
  if (typeof errorData.message === 'string') return errorData.message;
  if (errorData.error && typeof errorData.error === 'object') {
    if (typeof errorData.error.message === 'string') return errorData.error.message;
    if (typeof errorData.error.detail === 'string') return errorData.error.detail;
  }
  return fallback;
};

/**
 * Validate that a string is a valid JWT format
 * JWT format: header.payload.signature (three base64url-encoded parts separated by dots)
 */
export const isValidJWT = (token: string): boolean => {
  if (!token || typeof token !== 'string') {
    return false;
  }

  // JWT should have exactly 3 parts separated by dots
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }

  // Each part should be base64url encoded (alphanumeric, dash, underscore)
  const base64UrlPattern = /^[A-Za-z0-9_-]+$/;
  return parts.every(part => base64UrlPattern.test(part) && part.length > 0);
};

/**
 * Get the base API URL based on environment
 */
export const getApiUrl = (): string => {
  const configUrl = getConfig().apiUrl;
  if (configUrl) {
    let url = configUrl;
    // Enforce HTTPS when page is served over HTTPS (prevents mixed content)
    if (window.location.protocol === 'https:') {
      url = url.replace(/^http:\/\//, 'https://');
    }
    return url;
  }

  // Fallback: derive from current hostname
  const host = window.location.hostname;
  const port = process.env.NODE_ENV === 'production' ? window.location.port : '8000';
  const apiHost = port ? `${host}:${port}` : host;
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';

  return `${protocol}//${apiHost}`;
};

/**
 * Get WebSocket URL for a given path.
 * Routes directly to the backend API URL (same host as HTTP API).
 * CRA's webpack-dev-server intercepts WS upgrades for HMR, so we
 * bypass it entirely by connecting to backend port directly.
 */
export const getWebSocketUrl = (path: string): string => {
  let apiHost: string;

  const configUrl = getConfig().apiUrl;
  if (configUrl) {
    apiHost = configUrl;
  } else {
    // Use current hostname with backend port
    const host = window.location.hostname;
    const port = process.env.NODE_ENV === 'production' ? window.location.port : '8000';
    apiHost = port ? `${host}:${port}` : host;
  }

  // Determine protocol based on current page
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

  // Remove any existing protocol from apiHost
  const cleanHost = apiHost.replace(/^https?:\/\//, '').replace(/^wss?:\/\//, '');

  return `${wsProtocol}//${cleanHost}${path}`;
};

/**
 * Make authenticated API request
 */
export const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = localStorage.getItem('authToken');
  
  const headers = {
    ...options.headers,
    'Authorization': token ? `Bearer ${token}` : '',
  };
  
  return fetch(`${getApiUrl()}${url}`, {
    ...options,
    headers,
  });
};